import { useState } from "react";
import { Link } from "@/lib/router";
import { useRouter } from "@/lib/router";
import { createClient } from "@/lib/supabase/client";
import { authErrorMessage } from "@/lib/auth/messages";
import { LEGAL, type ConsentRecord } from "@/lib/legal/config";
import { checkPasswordRules, isPasswordValid, MIN_PASSWORD_LENGTH, PASSWORD_RULES } from "@/lib/auth/password-rules";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { SignUpSuccessDialog } from "@/components/sign-up-success-dialog";
import { Check, X } from "lucide-react";

const CONSENT_ITEMS = [
  { key: "ageOver14", label: "만 14세 이상입니다.", href: null },
  { key: "terms", label: "이용약관에 동의합니다.", href: "/terms" },
  { key: "privacy", label: "개인정보 수집·이용에 동의합니다.", href: "/privacy#1" },
  // 민감정보 처리와 국외 이전은 각각 구분해서 동의를 받는다(개인정보 보호법 제22조).
  {
    key: "sensitive",
    label: "신앙 관련 민감정보(설교 노트) 처리에 동의합니다.",
    href: "/privacy#1",
  },
  {
    key: "overseas",
    label: "노트 분석·검색을 위한 국외 AI 서비스(Google, OpenAI 등) 전송에 동의합니다.",
    href: "/privacy#3",
  },
] as const;

type ConsentKey = (typeof CONSENT_ITEMS)[number]["key"];
const NO_CONSENT: Record<ConsentKey, boolean> = {
  ageOver14: false,
  terms: false,
  privacy: false,
  sensitive: false,
  overseas: false,
};

/**
 * 이메일+비밀번호 회원가입 폼. 가입하면 Supabase가 인증 메일을 보내고(대시보드의
 * "Confirm email"이 켜져 있을 때), 사용자가 링크를 누르면 /auth/confirm이
 * 세션을 만들어 홈으로 보낸다. public.users 프로필 행은 DB 트리거
 * (handle_new_auth_user)가 자동으로 만든다.
 */
export function SignUpForm() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [consent, setConsent] = useState(NO_CONSENT);
  const [showSuccess, setShowSuccess] = useState(false);
  const allAgreed = CONSENT_ITEMS.every((item) => consent[item.key]);
  const passwordValid = isPasswordValid(password);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const passwordCheck = checkPasswordRules(password);
    if (!passwordCheck.every((r) => r.passed)) {
      setError("비밀번호 조건을 모두 만족해주세요.");
      return;
    }
    if (password !== passwordConfirm) {
      setError("비밀번호가 서로 달라요.");
      return;
    }

    if (!allAgreed) {
      setError("필수 항목에 모두 동의해야 가입할 수 있어요.");
      return;
    }

    setIsLoading(true);
    const supabase = createClient();
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/confirm?next=/`,
        // 어떤 버전에 언제 동의했는지 남긴다. 인증 메일 확인 전이라 세션이 없어서
        // 테이블 insert 대신 auth user_metadata에 저장한다(클라이언트 값이라 감사용
        // 증빙으로는 약하다 — 엄격한 기록이 필요하면 user_consents 테이블로 확장).
        data: {
          consent: {
            version: LEGAL.version,
            agreedAt: new Date().toISOString(),
            terms: true,
            privacy: true,
            sensitive: true,
            overseas: true,
            ageOver14: true,
          } satisfies ConsentRecord,
        },
      },
    });

    if (error) {
      setError(authErrorMessage(error));
      setIsLoading(false);
      return;
    }

    // Supabase는 이미 가입(인증완료)된 이메일로 signUp을 다시 호출해도 에러를 던지지
    // 않는다 — 이메일 존재 여부를 외부에 노출하지 않으려는 보안 설계다. 대신 이 경우
    // data.user.identities가 빈 배열로 온다(문서화된 유일한 구분 방법). 이걸 안 걸러내면
    // 실제로는 메일이 안 갔는데도 "메일함을 확인해주세요" 모달이 떠서 사용자가 헷갈린다.
    if (data.user && data.user.identities?.length === 0) {
      setError("이미 가입된 이메일이에요. 로그인해주세요.");
      setIsLoading(false);
      return;
    }

    // 이메일 인증이 꺼져 있으면 가입 즉시 세션이 생긴다 — 바로 홈으로.
    if (data.session) {
      router.push("/");
      router.refresh();
      return;
    }

    setShowSuccess(true);
  }

  return (
    <Card className="w-full max-w-sm border-0 bg-transparent shadow-none ring-0">
      <CardHeader className="items-center text-center">
        <CardTitle className="text-2xl">회원가입</CardTitle>
        <CardDescription>이메일로 계정을 만드세요</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="email">이메일</Label>
            <Input
              className="h-11"
              id="email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="password">비밀번호</Label>
            <Input
              className="h-11"
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {password.length > 0 ? (
              <ul className="flex flex-col gap-1 text-xs">
                {checkPasswordRules(password).map((rule) => (
                  <li
                    key={rule.label}
                    className={
                      rule.passed ? "flex items-center gap-1.5 text-emerald-600 dark:text-emerald-500" : "flex items-center gap-1.5 text-muted-foreground"
                    }
                  >
                    {rule.passed ? <Check className="size-3.5" /> : <X className="size-3.5" />}
                    {rule.label}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground">
                {PASSWORD_RULES.map((r) => r.label).join(" · ")}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="password-confirm">비밀번호 확인</Label>
            <Input
              className="h-11"
              id="password-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
            />
          </div>

          <fieldset className="flex flex-col gap-2 rounded-md border border-border p-3">
            <legend className="px-1 text-xs text-muted-foreground">필수 동의</legend>
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={allAgreed}
                onChange={(e) =>
                  setConsent({
                    ageOver14: e.target.checked,
                    terms: e.target.checked,
                    privacy: e.target.checked,
                    sensitive: e.target.checked,
                    overseas: e.target.checked,
                  })
                }
              />
              전체 동의
            </label>
            {CONSENT_ITEMS.map((item) => (
              <div key={item.key} className="flex items-start gap-2 text-sm">
                <label className="flex flex-1 items-start gap-2 text-muted-foreground">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 shrink-0 accent-primary"
                    checked={consent[item.key]}
                    onChange={(e) => setConsent((c) => ({ ...c, [item.key]: e.target.checked }))}
                  />
                  <span>(필수) {item.label}</span>
                </label>
                {item.href ? (
                  <Link
                    href={item.href}
                    target="_blank"
                    className="shrink-0 text-xs underline underline-offset-4"
                  >
                    보기
                  </Link>
                ) : null}
              </div>
            ))}
          </fieldset>

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          <Button type="submit" disabled={isLoading || !allAgreed || !passwordValid} className="h-11 w-full rounded-full">
            {isLoading ? "가입 중..." : "회원가입"}
          </Button>

          <p className="text-center text-sm text-muted-foreground">
            이미 계정이 있으신가요?{" "}
            <Link href="/auth/login" className="text-foreground underline underline-offset-4">
              로그인
            </Link>
          </p>
        </form>
      </CardContent>
      <SignUpSuccessDialog open={showSuccess} />
    </Card>
  );
}
